'use client';

import { motion } from 'framer-motion';
import { cn } from 'lib/cn';
import { useLayoutEffect, useRef, useState } from 'react';

export function ElegantShape({
    className,
    delay = 0,
    width = 400,
    height = 100,
    rotate = 0,
    gradient = 'tw-from-white/[0.08]'
}) {
    const baseTransition = {
        duration: 3.4,
        ease: [0.23, 0.86, 0.39, 0.96] as const
    };

    const enterTransition = {
        ...baseTransition,
        delay
    };

    const ref = useRef<HTMLDivElement>(null);
    const [exitX, setExitX] = useState(0);
    const [exitY, setExitY] = useState(0);
    const [exitRotate, setExitRotate] = useState(0);

    useLayoutEffect(() => {
        const updateExitDirection = () => {
            if (!ref.current) return;

            const rect = ref.current.getBoundingClientRect();
            const elementCenterX = rect.left + rect.width / 2;
            const elementCenterY = rect.top + rect.height / 2;
            const viewportCenterX = window.innerWidth / 2;
            const viewportCenterY = window.innerHeight / 2;
            const deltaX = viewportCenterX - elementCenterX;
            const deltaY = viewportCenterY - elementCenterY;
            const travelDistance = Math.hypot(window.innerWidth, window.innerHeight) * 1.25;
            const vectorLength = Math.hypot(deltaX, deltaY) || 1;
            let targetRotate = (Math.atan2(deltaY, deltaX) * 180) / Math.PI;

            // The shape is symmetric, so keep the target angle close to its current rotation.
            if (targetRotate > 90) targetRotate -= 180;
            if (targetRotate < -90) targetRotate += 180;

            setExitX((deltaX / vectorLength) * travelDistance);
            setExitY((deltaY / vectorLength) * travelDistance);
            setExitRotate(targetRotate);
        };

        updateExitDirection();
        window.addEventListener('resize', updateExitDirection);

        return () => {
            window.removeEventListener('resize', updateExitDirection);
        };
    }, [rotate]);

    return (
        <motion.div
            ref={ref}
            initial={{
                opacity: 0,
                y: -150,
                rotate: rotate - 15
            }}
            animate={{
                opacity: 1,
                y: 0,
                rotate
            }}
            exit={{
                x: [0, 0, -exitX],
                y: [0, 0, -exitY],
                rotate: [rotate, exitRotate, exitRotate],
                transition: {
                    ...baseTransition,
                    times: [0, 0.35, 1]
                }
            }}
            transition={enterTransition}
            className={cn('tw-absolute', className)}
        >
            <motion.div
                animate={{
                    y: [0, 15, 0]
                }}
                transition={{
                    duration: 12,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: 'easeInOut'
                }}
                style={{ width, height }}
                className="tw-relative"
            >
                <div
                    className={cn(
                        'tw-absolute tw-inset-0 tw-rounded-full',
                        'tw-bg-gradient-to-r tw-to-success/[0.15]',
                        gradient,
                        'tw-border tw-border-solid tw-border-success/20 tw-backdrop-blur-[2px]',
                        'tw-shadow-xl tw-shadow-success/30'
                    )}
                />
            </motion.div>
        </motion.div>
    );
}
